// src/pages/Home.jsx
import { useCallback, useEffect, useState } from "react";
import { getCurrentUser, signOut } from "aws-amplify/auth";
import { list, getUrl } from 'aws-amplify/storage';
import { useNavigate, Link } from "react-router-dom";
import LogoAnimation from "../components/LogoAnimation";
import Partenaires from "../components/Partenaires";
import LiensLegaux from "../components/LiensLegaux";
import SelecteurLangue from "../components/SelecteurLangue";
import { useLangue, useTextes } from "../i18n/Langue";
import textes from "./Home.textes";
import { noteGlobale, nbAvis, lienGoogle, avis } from "../data/avis";
import { AMBIANCE_PREFIX, EVENEMENTS_PREFIX, splitEvenements } from "../utils/affiches";
import "./Home.css";

/* ─────────────────────────────────────────────────────────────
   Hook scroll reveal — se relance quand les dépendances changent
   (notamment isLoggedIn qui fait apparaître le bloc admin)
───────────────────────────────────────────────────────────── */
function useScrollReveal(deps = []) {
  useEffect(() => {
    // Petit délai pour laisser React finir le rendu du DOM
    const timer = setTimeout(() => {
      const items = document.querySelectorAll('[data-animate]');

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('anim-visible');
              entry.target.classList.remove('anim-exit');
            } else {
              entry.target.classList.remove('anim-visible');
              entry.target.classList.add('anim-exit');
            }
          });
        },
        { threshold: 0.14, rootMargin: '0px 0px -40px 0px' }
      );

      items.forEach((el) => observer.observe(el));
      return () => observer.disconnect();
    }, 80);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function Home() {
  const [isLoggedIn, setIsLoggedIn]           = useState(false);
  const [ambiancePhotos, setAmbiancePhotos]   = useState([]);
  const [evenementPhotos, setEvenementPhotos] = useState([]);
  const [lightboxOpen, setLightboxOpen]       = useState(false);
  const [lightboxImages, setLightboxImages]   = useState([]);
  const [lightboxIndex, setLightboxIndex]     = useState(0);
  const navigate = useNavigate();
  const t = useTextes(textes);
  const { locale } = useLangue();

  // Se relance quand isLoggedIn change → observe le bloc admin dès qu'il apparaît
  useScrollReveal([isLoggedIn]);

  /* ── Lightbox ─────────────────────────────────────────────── */
  const openLightbox = useCallback((images, index) => {
    if (!images?.length) return;
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  const prev = useCallback(() =>
    setLightboxIndex((i) => (i - 1 + lightboxImages.length) % lightboxImages.length),
    [lightboxImages.length]);

  const next = useCallback(() =>
    setLightboxIndex((i) => (i + 1) % lightboxImages.length),
    [lightboxImages.length]);

  /* ── Auth ─────────────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      try { await getCurrentUser(); setIsLoggedIn(true); }
      catch { setIsLoggedIn(false); }
    })();
  }, []);

  /* ── Clavier lightbox ─────────────────────────────────────── */
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape')     closeLightbox();
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightboxOpen, closeLightbox, prev, next]);

  /* ── Photos AWS ───────────────────────────────────────────── */
  useEffect(() => {
    const fetchSection = async (prefix) => {
      const options = { accessLevel: 'public' };
      let res;
      try { res = await list({ prefix, options }); }
      catch { res = await list({ path: prefix, options }); }

      const items = res?.items ?? res?.results ?? [];
      const keys = items
        .map((it) => it?.key ?? it?.path ?? it)
        .filter((k) => typeof k === 'string' && k.length > 0 && !k.endsWith('/'));

      // La clé S3 est conservée : elle porte la date de l'événement
      const withUrls = await Promise.all(
        keys.map(async (key) => {
          try {
            const r = await getUrl({ key, options });
            return { key, url: r?.url?.toString?.() ?? r?.url ?? r };
          } catch {
            const r = await getUrl({ path: key, options });
            return { key, url: r?.url?.toString?.() ?? r?.url ?? r };
          }
        })
      );
      return withUrls.filter((x) => x.url);
    };

    (async () => {
      try {
        const [a, e] = await Promise.all([
          fetchSection(AMBIANCE_PREFIX),
          fetchSection(EVENEMENTS_PREFIX)
        ]);
        setAmbiancePhotos(a.map((x) => x.url));
        // L'accueil ne montre que les événements à venir, le plus proche
        // en premier. Les passés restent sur la page Galerie.
        setEvenementPhotos(splitEvenements(e).aVenir.map((x) => x.url));
      } catch (err) {
        console.error('Erreur chargement photos:', err);
      }
    })();
  }, []);

  /* ── Auth click ───────────────────────────────────────────── */
  const handleAuthClick = async () => {
    if (isLoggedIn) {
      await signOut();
      setIsLoggedIn(false);
      navigate("/");
    } else {
      navigate("/login");
    }
  };

  /* ── Rendu ────────────────────────────────────────────────── */
  return (
    <div className="page-home light-theme">

      {/* ── HERO ─────────────────────────────────────────────── */}
      <header className="hero">
        <div
          className="hero-media"
          aria-hidden
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/hero-chalet.jpg)` }}
        />

        <div className="auth-wrapper">
          <SelecteurLangue sombre />
          <button className="btn btn-ghost auth-button" onClick={handleAuthClick}>
            {isLoggedIn ? t.deconnexion : t.connexionAdmin}
          </button>
        </div>

        <div className="hero-overlay">
          <div className="hero-brand">
            <div className="logo-shadow">
              <LogoAnimation />
            </div>
            <div className="tagline-plate">
              <p className="hero-tagline">
                {t.accrocheCuisine}
                <br />
                {t.accrocheEvenements}
              </p>
            </div>
          </div>
          <div className="hero-ctas">
            <Link to="/devis"       className="btn btn-primary">{t.ctaReserver}</Link>
            <Link to="/formules"    className="btn btn-primary">{t.ctaFormules}</Link>
            <Link to="/menus-carte" className="btn btn-primary">{t.ctaCarte}</Link>
          </div>
        </div>
      </header>

      {/* ── CONTENU ──────────────────────────────────────────── */}
      <main className="content-white">

        {/* ── NOS SERVICES ──────────────────────────────────── */}
        <section className="container home-section" aria-labelledby="home-start">

          <div className="section-head" data-animate data-delay="0">
            <h2 id="home-start" className="section-title">{t.services}</h2>
          </div>

          <div className="home-panels">
            <div className="home-panel" data-animate data-delay="1">
              <h3 className="panel-title">{t.evenementsCommandes}</h3>
              <nav className="actions-grid actions-grid--primary">
                <Link to="/devis" className="action-card action-card--primary" data-animate data-delay="2">
                  <h3>{t.reserverTitre}</h3>
                  <p>{t.reserverTexte}</p>
                  <span className="action-more">{t.reserverAction}</span>
                </Link>
                <Link to="/commande" className="action-card action-card--primary" data-animate data-delay="3">
                  <h3>{t.commanderTitre}</h3>
                  <p>{t.commanderTexte}</p>
                  <span className="action-more">{t.commanderAction}</span>
                </Link>
                <Link to="/traiteur" className="action-card action-card--primary" data-animate data-delay="4">
                  <h3>{t.traiteurTitre}</h3>
                  <p>{t.traiteurTexte}</p>
                  <span className="action-more">{t.traiteurAction}</span>
                </Link>
              </nav>
            </div>

            <div className="home-panel" data-animate data-delay="2">
              <h3 className="panel-title">{t.infos}</h3>
              <div className="info-grid" aria-label={t.infos}>
                <article className="info-card" data-animate data-delay="3">
                  <div className="info-icon" aria-hidden>📍</div>
                  <div>
                    <h3 className="info-title">{t.adresse}</h3>
                    <p>98 Avenue de Choisy — 94190 Villeneuve-Saint-Georges</p>
                    <a className="link" href="https://maps.google.com/?q=Le+Chalet+des+Flandres" target="_blank" rel="noreferrer">
                      {t.itineraire}
                    </a>
                  </div>
                </article>
                <article className="info-card" data-animate data-delay="4">
                  <div className="info-icon" aria-hidden>🕒</div>
                  <div>
                    <h3 className="info-title">{t.horaires}</h3>
                    <p>{t.horairesTexte}</p>
                    <Link to="/contact" className="link">{t.plusInfos}</Link>
                  </div>
                </article>
                <article className="info-card" data-animate data-delay="5">
                  <div className="info-icon" aria-hidden>☎️</div>
                  <div>
                    <h3 className="info-title">{t.contact}</h3>
                    <p>
                      <a className="link" href="tel:+33767608186">07 67 60 81 86</a>
                      {" · "}
                      <a className="link" href="https://wa.me/33767608186" target="_blank" rel="noreferrer">WhatsApp</a>
                    </p>
                    <Link to="/devis" className="link">{t.demanderDevis}</Link>
                  </div>
                </article>
              </div>

              <div className="home-map" data-animate data-delay="6">
                <iframe
                  title={t.titreCarte}
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2630.4511156940994!2d2.4317407!3d48.754180899999994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e674bf70a02303%3A0x79314202b413c642!2sLe%20Chalet%20des%20Flandres!5e0!3m2!1sfr!2sfr!4v1781811744009!5m2!1sfr!2sfr"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>

          {/* ── BLOC ADMIN ── visible seulement si connecté ─── */}
          {isLoggedIn && (
            <div className="home-panel home-panel--admin" data-animate data-delay="1">
              <h3 className="panel-title">{t.administration}</h3>
              <nav className="actions-grid actions-grid--admin">
                <Link to="/gerer-devis" className="action-card" data-animate data-delay="2">
                  <h3>{t.adminDevisTitre}</h3>
                  <p>{t.adminDevisTexte}</p>
                  <span className="action-more">{t.ouvrir}</span>
                </Link>
                <Link to="/analytics" className="action-card" data-animate data-delay="3">
                  <h3>{t.adminAnalyticsTitre}</h3>
                  <p>{t.adminAnalyticsTexte}</p>
                  <span className="action-more">{t.ouvrir}</span>
                </Link>
                <Link to="/maquette" className="action-card" data-animate data-delay="3">
                  <h3>{t.adminMaquetteTitre}</h3>
                  <p>{t.adminMaquetteTexte}</p>
                  <span className="action-more">{t.ouvrir}</span>
                </Link>
                <Link to="/gerer-photos" className="action-card" data-animate data-delay="3">
                  <h3>{t.adminPhotosTitre}</h3>
                  <p>{t.adminPhotosTexte}</p>
                  <span className="action-more">{t.ouvrir}</span>
                </Link>
                <Link to="/admin-menu" className="action-card" data-animate data-delay="4">
                  <h3>{t.adminCarteTitre}</h3>
                  <p>{t.adminCarteTexte}</p>
                  <span className="action-more">{t.acceder}</span>
                </Link>
                <Link to="/code-admin" className="action-card" data-animate data-delay="5">
                  <h3>{t.adminCodeTitre}</h3>
                  <p>{t.adminCodeTexte}</p>
                  <span className="action-more">{t.voirCode}</span>
                </Link>
              </nav>
            </div>
          )}
        </section>

        {/* ── GALERIES ─────────────────────────────────────── */}
        <section className="container home-section" aria-labelledby="home-gallery">

          <div className="section-head" data-animate data-delay="0">
            <h2 id="home-gallery" className="section-title">{t.galeries}</h2>
            <p className="section-subtitle">{t.galeriesSousTitre}</p>
          </div>

          <div className="gallery-panels">
            <div className="gallery-panel" data-animate data-delay="1">
              <div className="panel-top">
                <h3 className="panel-title">{t.ambiance}</h3>
                <Link to="/galerie#ambiance" className="btn btn-outline">{t.voirTout}</Link>
              </div>
              <div className="gallery-grid">
                {(ambiancePhotos.length ? ambiancePhotos : Array.from({ length: 6 }))
                  .slice(0, 6)
                  .map((url, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="ph"
                      data-animate
                      data-delay={idx + 2}
                      onClick={() => { if (typeof url === 'string') openLightbox(ambiancePhotos, idx); }}
                      style={typeof url === 'string' ? { backgroundImage: `url(${url})` } : undefined}
                      aria-label={t.ouvrirPhoto}
                    />
                  ))}
              </div>
            </div>

            <div className="gallery-panel" data-animate data-delay="2">
              <div className="panel-top">
                <h3 className="panel-title">{t.evenementsAVenir}</h3>
                <Link to="/galerie#evenements" className="btn btn-outline">{t.voirTout}</Link>
              </div>
              <div className="gallery-grid">
                {(evenementPhotos.length ? evenementPhotos : Array.from({ length: 6 }))
                  .slice(0, 6)
                  .map((url, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="ph"
                      data-animate
                      data-delay={idx + 3}
                      onClick={() => { if (typeof url === 'string') openLightbox(evenementPhotos, idx); }}
                      style={typeof url === 'string' ? { backgroundImage: `url(${url})` } : undefined}
                      aria-label={t.ouvrirAffiche}
                    />
                  ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── PARTENAIRES ──────────────────────────────────── */}
        <section className="container home-section" aria-labelledby="home-partenaires">

          <div className="section-head" data-animate data-delay="0">
            <h2 id="home-partenaires" className="section-title">{t.partenaires}</h2>
            <p className="section-subtitle">{t.partenairesSousTitre}</p>
          </div>

          <div data-animate data-delay="1">
            <Partenaires showHeader={false} />
          </div>
        </section>

        {/* ── AVIS GOOGLE ──────────────────────────────────── */}
        <section className="container home-section" aria-labelledby="home-avis">

          <div className="section-head" data-animate data-delay="0">
            <h2 id="home-avis" className="section-title">{t.avis}</h2>
            <p className="section-subtitle">{t.avisSousTitre}</p>
          </div>

          <div className="avis-rating-banner" data-animate data-delay="1">
            <div className="avis-rating-score">
              <span className="avis-rating-number">{noteGlobale.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
              <div className="avis-rating-stars" aria-label={t.note(noteGlobale)}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <span key={i} className={i < Math.round(noteGlobale) ? 'star star--full' : 'star'}>★</span>
                ))}
              </div>
              {nbAvis > 0 && <span className="avis-rating-count">{t.nbAvis(nbAvis)}</span>}
            </div>
            <a href={lienGoogle} target="_blank" rel="noreferrer" className="btn btn-primary avis-rating-cta">
              {t.voirAvis}
            </a>
          </div>

          <div className="avis-grid">
            {avis.map((a, idx) => (
              <article key={idx} className="avis-card" data-animate data-delay={idx + 2}>
                <div className="avis-card-stars">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i} className={i < a.note ? 'star star--full' : 'star'}>★</span>
                  ))}
                </div>
                <p className="avis-card-text">“{a.texte}”</p>
                <div className="avis-card-footer">
                  <span className="avis-card-author">{a.auteur}</span>
                  {a.date && <span className="avis-card-date">{a.date}</span>}
                </div>
              </article>
            ))}
          </div>

        </section>

        <LiensLegaux />

      </main>

      {/* ── LIGHTBOX ─────────────────────────────────────────── */}
      {lightboxOpen && lightboxImages.length > 0 && (
        <div className="lightbox-overlay" role="dialog" aria-modal="true" onClick={closeLightbox}>
          <div className="lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="lightbox-close" onClick={closeLightbox} aria-label={t.fermer}>×</button>
            <button type="button" className="lightbox-nav left"  onClick={prev} aria-label={t.precedent}>‹</button>
            <img className="lightbox-img" src={lightboxImages[lightboxIndex]} alt="" onClick={closeLightbox} />
            <button type="button" className="lightbox-nav right" onClick={next} aria-label={t.suivant}>›</button>
          </div>
        </div>
      )}

      {/* ── BANDROLL ─────────────────────────────────────────── */}
      <aside className="bandroll fixed" aria-label={t.reseaux}>
        <div className="bandroll-inner">
          <span className="bandroll-label">{t.suivezNous}</span>
          <nav className="bandroll-links">
            <a className="social-pill" href="https://www.facebook.com/share/1F7EYKvc8E/?mibextid=wwXIfr" target="_blank" rel="noreferrer">Facebook</a>
            <a className="social-pill" href="https://www.instagram.com/lechaletdesflandres?igsh=MTY2amN0YWdvMGNlcg==" target="_blank" rel="noreferrer">Instagram</a>
            <a className="social-pill" href="https://www.tiktok.com/@lechaletdesflandres?_t=ZN-8zl5KUKe55D&_r=1" target="_blank" rel="noreferrer">TikTok</a>
          </nav>
        </div>
      </aside>

    </div>
  );
}

export default Home;
